import React, { useMemo, useState } from 'react';
import { Plus, Lock, Unlock, Printer, Eye, Trash2, Pencil, User as UserIcon, AlertCircle, CheckCircle2, Scale, Check, X } from 'lucide-react';
import { allocationsApi, ordersApi, type WorkOrderLineDto, type FarmerAllocationDto, type WorkOrderDto } from '../../../services/api';
import type { PalletPreviewDto, PackagingContainer } from '../../../types/loading-plan';
import { Button } from '../../../components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import { AddBoxPanel, type BoxEntry } from './AddBoxPanel';
import { PackagingWeightsPanel } from './PackagingWeightsPanel';

interface Props {
  orderId: string;
  pallet: PalletPreviewDto;
  orderLines: WorkOrderLineDto[];
  containers: PackagingContainer[];
  onChanged: () => void | Promise<void>;
}

type ClosingStage = null | 'saving' | 'printing' | 'closing' | 'done' | 'print-failed';

type AllocationPayload = {
  workOrderLineId: string;
  partida: string;
  bultos: number;
  quantity: number;
};

type PendingAction = {
  title: string;
  detail: string;
  run: () => Promise<void>;
} | null;

type EditingState = {
  allocationId: string;
  initial: BoxEntry;
} | null;

export const PalletColumn: React.FC<Props> = ({ orderId, pallet, orderLines, containers, onChanged }) => {
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<EditingState>(null);
  const [showWeights, setShowWeights] = useState(false);
  const [editingDeadWeight, setEditingDeadWeight] = useState(false);
  const [deadWeightDraft, setDeadWeightDraft] = useState<string>('');
  const [closingStage, setClosingStage] = useState<ClosingStage>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const isClosed = !!pallet.closed;
  const isManual = !!pallet.isManual;
  const deadWeight = pallet.deadWeightKg ?? 0;

  // Allocations del palet, reconstruidas desde las líneas de la comanda.
  const allocations = useMemo(() => {
    const rows: Array<FarmerAllocationDto & { workOrderLineId: string; productName: string }> = [];
    for (const line of orderLines) {
      for (const a of line.allocations ?? []) {
        if (a.virtualPalletId === pallet.id) {
          rows.push({ ...a, workOrderLineId: line.id, productName: line.productName ?? '—' });
        }
      }
    }
    return rows;
  }, [orderLines, pallet.id]);

  const totals = useMemo(() => {
    const bultos = allocations.reduce((s, a) => s + a.bultos, 0);
    const kg = allocations.reduce((s, a) => s + a.quantity, 0);
    return { bultos, kg };
  }, [allocations]);

  /**
   * Calcula overflow contra un snapshot fresco del backend. El payload reemplaza
   * íntegramente las allocations de este palet, así que el total post-operación
   * para cada línea es: (suma de payload) + (suma de allocations en OTROS palets
   * según el snapshot).
   */
  const findOverflowsAgainst = (payload: AllocationPayload[], freshOrder: WorkOrderDto) => {
    const lines = freshOrder.lines ?? [];
    const grouped = new Map<string, number>();
    for (const p of payload) {
      grouped.set(p.workOrderLineId, (grouped.get(p.workOrderLineId) ?? 0) + p.bultos);
    }
    const out: Array<{ lineId: string; productName: string; assigned: number; requested: number }> = [];
    for (const [lineId, sumInThisPallet] of grouped) {
      const line = lines.find(l => l.id === lineId);
      if (!line) continue;
      const requested = line.quantity ?? 0;
      const inOtherPallets = (line.allocations ?? [])
        .filter(a => a.virtualPalletId !== pallet.id)
        .reduce((s, a) => s + a.bultos, 0);
      const totalAssigned = sumInThisPallet + inOtherPallets;
      if (totalAssigned > requested) {
        out.push({ lineId, productName: line.productName ?? '—', assigned: totalAssigned, requested });
      }
    }
    return out;
  };

  const toPayload = (a: FarmerAllocationDto & { workOrderLineId: string }): AllocationPayload => ({
    workOrderLineId: a.workOrderLineId,
    partida: a.partida,
    bultos: a.bultos,
    quantity: a.quantity,
  });

  const persist = async (payload: AllocationPayload[]) => {
    await allocationsApi.setPalletAllocations(orderId, pallet.id, { allocations: payload });
    await onChanged();
  };

  /**
   * Persiste el payload pero antes consulta al backend el estado real de la
   * comanda para detectar overflow. Esto evita falsos negativos cuando otro
   * mozo añadió cajas en otro palet y el front local no se ha refrescado aún.
   * Si hay overflow, abre el AlertDialog y espera confirmación.
   */
  const persistWithOverflowGuard = async (payload: AllocationPayload[], context: string): Promise<void> => {
    setError(null);

    let freshOrder: WorkOrderDto;
    setBusy(true);
    try {
      freshOrder = await ordersApi.getById(orderId);
    } catch (e: any) {
      setError(e?.message || 'No se pudo verificar el estado actual.');
      setBusy(false);
      return;
    }
    setBusy(false);

    const overflows = findOverflowsAgainst(payload, freshOrder);

    const runPersist = async () => {
      setBusy(true);
      try { await persist(payload); }
      catch (e: any) { setError(e?.message || `No se pudo ${context}.`); }
      finally { setBusy(false); }
    };

    if (overflows.length === 0) {
      await runPersist();
      return;
    }

    const detail = overflows
      .map(o => `${o.productName}: ${o.assigned} cajas (pedido ${o.requested})`)
      .join(' · ');

    return new Promise<void>(resolve => {
      cancelResolveRef.current = resolve;
      setPendingAction({
        title: 'Excederás lo pedido',
        detail,
        run: async () => {
          await runPersist();
          resolve();
        },
      });
    });
  };

  // Ref para resolver la promesa de persistWithOverflowGuard si el usuario cancela.
  const cancelResolveRef = React.useRef<(() => void) | null>(null);

  // ── Mutaciones ───────────────────────────────────────────────────────────

  /**
   * Añadir caja. Si ya existe una allocation con misma (línea, partida), suma
   * los bultos y kg al registro existente en vez de crear uno nuevo.
   */
  const handleAddBox = async (entry: BoxEntry) => {
    const trimmedPartida = entry.partida.trim();
    const dup = allocations.find(a =>
      a.workOrderLineId === entry.workOrderLineId &&
      a.partida.trim().toLowerCase() === trimmedPartida.toLowerCase()
    );

    let payload: AllocationPayload[];
    if (dup) {
      payload = allocations.map(a =>
        a.id === dup.id
          ? { ...toPayload(a), bultos: a.bultos + entry.bultos, quantity: a.quantity + entry.quantity }
          : toPayload(a)
      );
    } else {
      payload = [...allocations.map(toPayload), { ...entry, partida: trimmedPartida }];
    }
    await persistWithOverflowGuard(payload, 'añadir la caja');
  };

  const handleEditBox = async (entry: BoxEntry) => {
    if (!editing) return;
    const id = editing.allocationId;
    const payload: AllocationPayload[] = allocations.map(a =>
      a.id === id
        ? { workOrderLineId: entry.workOrderLineId, partida: entry.partida.trim(), bultos: entry.bultos, quantity: entry.quantity }
        : toPayload(a)
    );
    await persistWithOverflowGuard(payload, 'actualizar la caja');
  };

  const handleSaveDeadWeight = async () => {
    const value = Math.max(0, Number(deadWeightDraft) || 0);
    if (value === deadWeight) { setEditingDeadWeight(false); return; }
    setError(null);
    setBusy(true);
    try {
      await allocationsApi.setDeadWeight(orderId, pallet.id, value);
      await onChanged();
      setEditingDeadWeight(false);
    } catch (e: any) {
      setError(e?.message || 'No se pudo guardar el peso muerto.');
    } finally {
      setBusy(false);
    }
  };

  const handleRemoveAllocation = async (allocationId: string) => {
    const payload = allocations.filter(a => a.id !== allocationId).map(toPayload);
    setError(null);
    setBusy(true);
    try { await persist(payload); }
    catch (e: any) { setError(e?.message || 'No se pudo eliminar la caja.'); }
    finally { setBusy(false); }
  };

  // ── Cierre / impresión ───────────────────────────────────────────────────

  const handleCloseAndPrint = async () => {
    setError(null);
    try {
      setClosingStage('saving');
      await allocationsApi.setPalletAllocations(orderId, pallet.id, {
        allocations: allocations.map(toPayload),
      });

      setClosingStage('printing');
      try {
        await allocationsApi.printPalletLabel(orderId, pallet.id);
      } catch (e: any) {
        setClosingStage('print-failed');
        setError(e?.message || 'La etiqueta no se pudo imprimir. El palet sigue abierto.');
        return;
      }

      setClosingStage('closing');
      await allocationsApi.closePallet(orderId, pallet.id);
      setClosingStage('done');
      await onChanged();
    } catch (e: any) {
      setError(e?.message || 'No se pudo cerrar el palet.');
      setClosingStage(null);
    }
  };

  const handleDelete = async () => {
    setConfirmingDelete(false);
    setError(null);
    setBusy(true);
    try {
      await allocationsApi.deletePallet(orderId, pallet.id);
      // El palet desaparece de la lista: el padre recarga y desmonta esta columna.
      await onChanged();
    } catch (e: any) {
      setError(e?.message || 'No se pudo eliminar el palet.');
      setBusy(false);
    }
  };

  const handleReopen = async () => {
    setError(null);
    setBusy(true);
    try {
      await allocationsApi.reopenPallet(orderId, pallet.id);
      setClosingStage(null);
      await onChanged();
    } catch (e: any) {
      setError(e?.message || 'No se pudo reabrir el palet.');
    } finally {
      setBusy(false);
    }
  };

  const handleReprint = async () => {
    setError(null);
    setBusy(true);
    try {
      await allocationsApi.printPalletLabel(orderId, pallet.id);
    } catch (e: any) {
      setError(e?.message || 'No se pudo reimprimir la etiqueta.');
    } finally {
      setBusy(false);
    }
  };

  const handlePreview = async () => {
    setError(null);
    setBusy(true);
    try {
      await allocationsApi.previewPalletLabel(orderId, pallet.id);
    } catch (e: any) {
      setError(e?.message || 'No se pudo abrir la vista previa.');
    } finally {
      setBusy(false);
    }
  };

  const closingBanner = (() => {
    switch (closingStage) {
      case 'saving': return { text: 'Guardando…', tone: 'bg-slate-100 text-slate-700' };
      case 'printing': return { text: 'Imprimiendo etiqueta…', tone: 'bg-blue-50 text-blue-800' };
      case 'closing': return { text: 'Cerrando palet…', tone: 'bg-slate-100 text-slate-700' };
      case 'done': return { text: 'Palet cerrado ✓', tone: 'bg-green-50 text-green-800' };
      case 'print-failed': return { text: 'Impresión fallida. Palet sigue abierto.', tone: 'bg-amber-50 text-amber-900' };
      default: return null;
    }
  })();

  return (
    <div className={`w-80 shrink-0 bg-white rounded-lg border-2 ${isClosed ? 'border-slate-300' : 'border-green-500'} flex flex-col shadow-sm h-full`}>
      {/* Cabecera */}
      <div className={`px-4 py-3 border-b ${isClosed ? 'bg-slate-50 border-slate-200' : 'bg-green-50 border-green-100'}`}>
        <div className="flex items-baseline justify-between">
          <h2 className="text-2xl font-bold text-slate-900">PALET {pallet.sequenceNumber}</h2>
          {isClosed && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-900 border border-amber-300">
              <Lock size={11} /> CERRADO
            </span>
          )}
        </div>
        {isManual && pallet.openedByUserName && (
          <p className="text-sm text-slate-600 flex items-center gap-1 mt-0.5">
            <UserIcon size={12} /> {pallet.openedByUserName}
          </p>
        )}
        <p className="text-sm text-slate-600 mt-1">
          <span className="text-xl font-bold text-slate-900 tabular-nums">{totals.bultos}</span> cajas ·{' '}
          <span className="text-xl font-bold text-slate-900 tabular-nums">{totals.kg.toFixed(1)}</span> kg
        </p>

        {/* Peso muerto + total */}
        <div className="mt-2 pt-2 border-t border-green-100/70 text-sm">
          {editingDeadWeight && !isClosed ? (
            <div className="flex items-center gap-1">
              <span className="text-slate-600 shrink-0">Peso muerto</span>
              <input
                autoFocus
                type="number"
                min={0}
                step={0.01}
                value={deadWeightDraft}
                onChange={e => setDeadWeightDraft(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') handleSaveDeadWeight();
                  if (e.key === 'Escape') setEditingDeadWeight(false);
                }}
                disabled={busy}
                className="ml-auto w-20 bg-white border border-slate-300 rounded px-2 py-0.5 text-base text-right tabular-nums focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
              <button
                type="button"
                onClick={handleSaveDeadWeight}
                disabled={busy}
                className="text-green-600 hover:text-green-800 p-1"
                aria-label="Guardar"
              >
                <Check size={14} />
              </button>
              <button
                type="button"
                onClick={() => setEditingDeadWeight(false)}
                disabled={busy}
                className="text-slate-400 hover:text-slate-700 p-1"
                aria-label="Cancelar"
              >
                <X size={14} />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-1">
              <span className="text-slate-600">Peso muerto:</span>
              <span className="ml-auto tabular-nums font-medium">{deadWeight.toFixed(2)} kg</span>
              {!isClosed && (
                <button
                  type="button"
                  onClick={() => {
                    setDeadWeightDraft(deadWeight.toString());
                    setEditingDeadWeight(true);
                  }}
                  disabled={busy}
                  className="text-slate-400 hover:text-blue-600 p-1"
                  aria-label="Editar peso muerto"
                  title="Editar peso muerto"
                >
                  <Pencil size={12} />
                </button>
              )}
            </div>
          )}
          <div className="flex justify-between mt-0.5 text-slate-700">
            <span>Total (cajas + muerto):</span>
            <span className="tabular-nums font-bold text-base">{(totals.kg + deadWeight).toFixed(2)} kg</span>
          </div>
        </div>
      </div>

      {/* Lista de cajas */}
      <div className="flex-1 overflow-y-auto px-3 py-2">
        {allocations.length === 0 ? (
          <p className="text-sm text-slate-400 italic text-center py-6">
            Palet vacío. Pulsa "+ Añadir caja".
          </p>
        ) : (
          <ul className="space-y-2">
            {allocations.map(a => (
              <li key={a.id} className="bg-slate-50 rounded p-2 border border-slate-200">
                <div className="flex items-start justify-between gap-1">
                  <div className="min-w-0">
                    <p className="text-base font-semibold text-slate-900 truncate uppercase">{a.productName}</p>
                    <p className="text-sm text-slate-600 font-mono">{a.partida}</p>
                    <p className="text-lg font-bold text-slate-900 tabular-nums mt-0.5">
                      {a.bultos}c · {a.quantity.toFixed(1)}kg
                    </p>
                  </div>
                  {!isClosed && (
                    <div className="flex flex-col gap-1 shrink-0">
                      <button
                        type="button"
                        onClick={() => setEditing({
                          allocationId: a.id,
                          initial: {
                            workOrderLineId: a.workOrderLineId,
                            partida: a.partida,
                            bultos: a.bultos,
                            quantity: a.quantity,
                          },
                        })}
                        disabled={busy}
                        className="text-slate-400 hover:text-blue-600 p-1 disabled:opacity-30"
                        aria-label="Editar caja"
                        title="Editar"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRemoveAllocation(a.id)}
                        disabled={busy}
                        className="text-slate-400 hover:text-red-600 p-1 disabled:opacity-30"
                        aria-label="Quitar caja"
                        title="Eliminar"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Estado de cierre / errores */}
      {(closingBanner || error) && (
        <div className="px-3 pb-2 space-y-1">
          {closingBanner && (
            <div className={`text-sm px-2 py-1.5 rounded flex items-center gap-1.5 ${closingBanner.tone}`}>
              {closingStage === 'done' && <CheckCircle2 size={14} />}
              {closingBanner.text}
            </div>
          )}
          {error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5 flex items-start gap-1.5">
              <AlertCircle size={14} className="shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>
      )}

      {/* Acciones */}
      <div className="px-3 pb-3 pt-2 border-t border-slate-100 space-y-2">
        {isClosed ? (
          <>
            <Button variant="outline" onClick={handlePreview} disabled={busy} className="w-full">
              <Eye size={14} /> Vista previa
            </Button>
            <Button onClick={handleReprint} disabled={busy} className="w-full bg-orange-600 hover:bg-orange-700 text-white">
              <Printer size={14} /> Reimprimir etiqueta
            </Button>
            <Button variant="outline" onClick={handleReopen} disabled={busy} className="w-full">
              <Unlock size={14} /> Reabrir palet
            </Button>
          </>
        ) : (
          <>
            <Button onClick={() => setAdding(true)} disabled={busy || closingStage !== null} className="w-full bg-green-600 hover:bg-green-700 text-white" size="lg">
              <Plus size={16} /> Añadir caja
            </Button>
            <Button variant="outline" onClick={() => setShowWeights(true)} className="w-full">
              <Scale size={14} /> Pesos envases
            </Button>
            <Button
              onClick={handleCloseAndPrint}
              disabled={busy || closingStage === 'saving' || closingStage === 'printing' || closingStage === 'closing' || allocations.length === 0}
              className="w-full"
              variant="outline"
            >
              <Lock size={14} /> Cerrar e imprimir
            </Button>
            <Button
              onClick={() => setConfirmingDelete(true)}
              disabled={busy || closingStage !== null}
              className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
              variant="ghost"
            >
              <Trash2 size={14} /> Eliminar palet
            </Button>
          </>
        )}
      </div>

      {adding && (
        <AddBoxPanel
          orderLines={orderLines}
          onClose={() => setAdding(false)}
          onSubmit={handleAddBox}
        />
      )}

      {editing && (
        <AddBoxPanel
          orderLines={orderLines}
          onClose={() => setEditing(null)}
          onSubmit={handleEditBox}
          initial={editing.initial}
        />
      )}

      {showWeights && (
        <PackagingWeightsPanel
          allocations={allocations}
          orderLines={orderLines}
          containers={containers}
          deadWeightKg={deadWeight}
          onClose={() => setShowWeights(false)}
        />
      )}

      <AlertDialog
        open={!!pendingAction}
        onOpenChange={open => {
          if (!open) {
            // Cierre por cancelación / click fuera → resolver promesa pendiente sin persistir
            cancelResolveRef.current?.();
            cancelResolveRef.current = null;
            setPendingAction(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{pendingAction?.title}</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingAction?.detail}. ¿Quieres guardar igualmente?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={async () => {
                const action = pendingAction;
                setPendingAction(null);
                cancelResolveRef.current = null;
                if (action) await action.run();
              }}
            >
              Guardar igualmente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmingDelete} onOpenChange={open => { if (!open) setConfirmingDelete(false); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar palet {pallet.sequenceNumber}</AlertDialogTitle>
            <AlertDialogDescription>
              {allocations.length > 0 ? (
                <>
                  Este palet contiene{' '}
                  <strong>{totals.bultos} {totals.bultos === 1 ? 'caja' : 'cajas'}</strong>{' '}
                  ({totals.kg.toFixed(1)} kg). Si lo eliminas, esas unidades quedarán
                  libres y podrás volver a asignarlas a otro palet. ¿Eliminar el palet?
                </>
              ) : (
                <>¿Eliminar este palet vacío? Esta acción no se puede deshacer.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleDelete}
            >
              Eliminar palet
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
